import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { styles, colors, radius } from '../styles/theme';
import { logError } from '../utils/logError';

// Header Implementation (Inline to avoid dependency issues if not exported)
const Header = ({ title }) => (
    <div style={{
        background: `linear-gradient(135deg, ${styles.primary} 0%, ${styles.primaryDark} 100%)`,
        color: 'white',
        padding: '15px 25px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    }}>
        <h1 style={{ margin: 0, fontSize: '22px' }}>{title}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <span style={{ fontSize: '14px' }}>{new Date().toLocaleDateString('tr-TR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
    </div>
);

const SistemAyarlariPage = () => {
    const [backupLoading, setBackupLoading] = useState(false);
    const [restoreLoading, setRestoreLoading] = useState(false);
    const [restoreFile, setRestoreFile] = useState(null);
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

    const handleBackup = async () => {
        setBackupLoading(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_URL}/api/system/backup`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                // Trigger download
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;

                // Content-Disposition header might be missing in CORS, try to guess or use default
                // But normally we let user save
                const contentDisposition = response.headers.get('Content-Disposition');
                let fileName = 'toruna_backup.sql';
                if (contentDisposition) {
                    const match = contentDisposition.match(/filename="?(.+)"?/);
                    if (match) fileName = match[1];
                } else {
                    // Fallback timestamp
                    fileName = `toruna_backup_${new Date().toISOString().slice(0, 10)}.sql`;
                }

                a.download = fileName;
                document.body.appendChild(a);
                a.click();
                a.remove();
                alert("Yedek başarıyla indirildi.");
            } else {
                const error = await response.json();
                alert(`Yedekleme hatası: ${error.detail || 'Bilinmeyen hata'}`);
            }
        } catch (error) {
            logError(error);
            alert("Bağlantı hatası");
        } finally {
            setBackupLoading(false);
        }
    };

    const handleRestore = async () => {
        if (!restoreFile) {
            alert("Lütfen bir yedek dosyası (.sql) seçin.");
            return;
        }

        const confirmRestore = window.confirm(
            "⚠️ DİKKAT: Veritabanını geri yüklemek mevcut TÜM verilerin silinmesine ve yedeğin üzerine yazılmasına neden olur.\n\nBu işlem geri alınamaz. Devam etmek istiyor musunuz?"
        );

        if (!confirmRestore) return;

        setRestoreLoading(true);
        try {
            const formData = new FormData();
            formData.append('file', restoreFile);

            const token = localStorage.getItem('access_token');
            const response = await fetch(`${API_URL}/api/system/restore`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                alert("Veritabanı başarıyla geri yüklendi! Sistem yeniden başlatılabilir.");
                window.location.reload();
            } else {
                const error = await response.json();
                alert(`Geri yükleme hatası: ${error.detail || 'Bilinmeyen hata'}`);
            }
        } catch (error) {
            logError(error);
            alert("Bağlantı hatası");
        } finally {
            setRestoreLoading(false);
            setRestoreFile(null);
        }
    };

    return (
        <div>
            <Header title="⚙️ Sistem Ayarları" />
            <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>

                {/* Tanılama / Diagnostik Section */}
                <div style={{ background: colors.surface, padding: '25px', borderRadius: radius.md, boxShadow: '0 2px 5px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <span style={{ fontSize: '32px' }}>🔗</span>
                        <div style={{ flex: 1 }}>
                            <h2 style={{ margin: 0, color: styles.secondary }}>Stok Referans Bütünlüğü</h2>
                            <p style={{ margin: '5px 0 0 0', color: colors.textSecondary }}>
                                Stok hareketlerinin kaynak belge bağlarını (yumuşak-FK) denetle —
                                orphan / sentinel / geçersiz tespiti (salt-okunur).
                            </p>
                        </div>
                        <Link
                            to="/ayarlar/stok-referans-butunlugu"
                            style={{
                                background: styles.primary,
                                color: 'white',
                                padding: '10px 18px',
                                borderRadius: radius.sm,
                                fontSize: '14px',
                                fontWeight: 'bold',
                                textDecoration: 'none',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Aç →
                        </Link>
                    </div>
                </div>

                {/* Backup Section */}
                <div style={{ background: colors.surface, padding: '25px', borderRadius: radius.md, boxShadow: '0 2px 5px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                        <span style={{ fontSize: '32px' }}>💾</span>
                        <div>
                            <h2 style={{ margin: 0, color: styles.secondary }}>Veritabanı Yedekleme</h2>
                            <p style={{ margin: '5px 0 0 0', color: colors.textSecondary }}>Mevcut veritabanının tam yedeğini (.sql) bilgisayarınıza indirin.</p>
                        </div>
                    </div>
                    <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '20px', textAlign: 'right' }}>
                        <button
                            onClick={handleBackup}
                            disabled={backupLoading}
                            style={{
                                background: backupLoading ? '#ccc' : styles.primary,
                                color: 'white',
                                padding: '12px 24px',
                                border: 'none',
                                borderRadius: radius.sm,
                                fontSize: '16px',
                                fontWeight: 'bold',
                                cursor: backupLoading ? 'not-allowed' : 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}
                        >
                            {backupLoading ? '⏳ Yedekleniyor...' : '⬇️ Yedek Al'}
                        </button>
                    </div>
                </div>

                {/* Restore Section */}
                <div style={{ background: colors.surface, padding: '25px', borderRadius: radius.md, boxShadow: '0 2px 5px rgba(0,0,0,0.1)', border: '2px solid #dc3545' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                        <span style={{ fontSize: '32px' }}>♻️</span>
                        <div>
                            <h2 style={{ margin: 0, color: '#dc3545' }}>Yekten Geri Yükleme (Restore)</h2>
                            <p style={{ margin: '5px 0 0 0', color: colors.textSecondary }}>
                                Önceden alınmış bir yedeği geri yükleyin.
                                <strong style={{ color: '#dc3545', display: 'block', marginTop: '5px' }}>⚠️ DİKKAT: Bu işlem mevcut verileri silecektir!</strong>
                            </p>
                        </div>
                    </div>

                    <div style={{ borderTop: `1px solid ${colors.border}`, paddingTop: '20px', display: 'flex', flexDirection: 'column', gap: '15px' }}>

                        <div style={{ padding: '15px', background: colors.backgroundAlt, border: '1px dashed #ccc', borderRadius: radius.sm }}>
                            <input
                                type="file"
                                accept=".sql"
                                onChange={(e) => setRestoreFile(e.target.files[0])}
                                style={{ width: '100%' }}
                            />
                            {restoreFile && (
                                <div style={{ marginTop: '10px', fontSize: '14px', color: styles.success, fontWeight: 'bold' }}>
                                    Dosya seçildi: {restoreFile.name}
                                </div>
                            )}
                        </div>

                        <div style={{ textAlign: 'right' }}>
                            <button
                                onClick={handleRestore}
                                disabled={restoreLoading || !restoreFile}
                                style={{
                                    background: restoreLoading || !restoreFile ? '#ccc' : '#dc3545',
                                    color: 'white',
                                    padding: '12px 24px',
                                    border: 'none',
                                    borderRadius: radius.sm,
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: restoreLoading || !restoreFile ? 'not-allowed' : 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}
                            >
                                {restoreLoading ? '⏳ Yükleniyor...' : '⚠️ Geri Yükle'}
                            </button>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default SistemAyarlariPage;
